import { RightOutlined } from '@ant-design/icons'
import { Collapse } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import type { EntityFilterType, StoreShortWithSupplierType } from '@/types'

import FilterCheckboxList from '../FilterCheckboxList/FilterCheckboxList'
import FilterOptionPills, {
  FilterOptionPillItem,
} from '../FilterOptionPills/FilterOptionPills'
import {
  formatCharacteristicValue,
  shouldRenderAsPills,
} from '../Filters.utils'
import generalCn from '../GeneralFiltersStyles.module.scss'
import HighlightText from '../HighlightText/HighlightText'

type FilterWithListPropsType<
  T extends EntityFilterType | StoreShortWithSupplierType,
> = {
  items: T[]
  selectedItems: T[]
  previouslySelectedItems: T[]
  title: string
  collapseItemKey: string
  searchInputPlaceholder: string
  itemsFetchError?: string
  setSelectedItems: (items: T[]) => void
  forceOpen?: boolean
  searchQuery?: string
  withPills?: boolean
}

function FilterWithList<
  T extends EntityFilterType | StoreShortWithSupplierType,
>(props: FilterWithListPropsType<T>) {
  const {
    items,
    selectedItems,
    previouslySelectedItems,
    title,
    collapseItemKey,
    searchInputPlaceholder,
    setSelectedItems,
    forceOpen = false,
    searchQuery = '',
    withPills = true,
  } = props

  const [searchValue, setSearchValue] = useState<string>('')
  const [itemsBySearch, setItemsBySearch] = useState<T[]>([])
  const [isOpen, setIsOpen] = useState(selectedItems.length > 0)

  useEffect(() => {
    if (selectedItems.length === 0) setIsOpen(false)
  }, [selectedItems.length])

  const displayItems = useMemo(() => {
    const combinedItemsMap = new Map<string, T>()

    items.forEach((item) => {
      combinedItemsMap.set(item.id, item)
    })

    previouslySelectedItems.forEach((item) => {
      if (
        !combinedItemsMap.has(item.id) &&
        selectedItems.some((sel) => sel.id === item.id)
      ) {
        const itemWithZeroCount = { ...item, count: 0 } as T
        combinedItemsMap.set(item.id, itemWithZeroCount)
      }
    })

    return Array.from(combinedItemsMap.values()).sort((a, b) => {
      const aSelected = selectedItems.some((sel) => sel.id === a.id) ? 0 : 1
      const bSelected = selectedItems.some((sel) => sel.id === b.id) ? 0 : 1
      return aSelected - bSelected
    })
  }, [items, selectedItems, previouslySelectedItems])

  useEffect(() => {
    setItemsBySearch(displayItems)
  }, [displayItems])

  const handleSearchItems = (value: string) => {
    setSearchValue(value)
    setItemsBySearch(
      displayItems.filter((item) =>
        item.description.toLowerCase().includes(value.toLowerCase())
      )
    )
  }

  const toggleItem = (item: T) => {
    if (!selectedItems.some((selectedItem) => selectedItem.id === item.id)) {
      setSelectedItems([...selectedItems, item])
    } else {
      setSelectedItems(
        selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
      )
    }
  }

  const usesPills = withPills && shouldRenderAsPills(displayItems.length)

  const pillItems: FilterOptionPillItem[] = displayItems.map((item) => ({
    key: item.id,
    label: (
      <HighlightText
        text={formatCharacteristicValue(item.description)}
        query={searchQuery}
      />
    ),
    selected: selectedItems.some((selectedItem) => selectedItem.id === item.id),
    disabled: 'count' in item && item.count === 0,
    count: 'count' in item ? item.count : undefined,
  }))

  return (
    <div className={generalCn.filterContainer}>
      <div className={generalCn.collapseContainer}>
        <Collapse
          activeKey={forceOpen || isOpen ? [collapseItemKey] : []}
          onChange={() => {
            if (!forceOpen) setIsOpen((prev) => !prev)
          }}
          expandIcon={({ isActive }) => (
            <RightOutlined
              className={`${generalCn.expandIcon} ${isActive ? generalCn.expandIconActive : ''}`}
            />
          )}
          items={[
            {
              key: collapseItemKey,
              label: (
                <div className={generalCn.collapseHeaderContainer}>
                  <span>
                    <HighlightText text={title} query={searchQuery} />
                  </span>
                  {selectedItems.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedItems([])
                      }}
                    >
                      Сбросить
                    </button>
                  )}
                </div>
              ),
              children: usesPills ? (
                <div className={generalCn.collapseItemContent}>
                  <FilterOptionPills
                    options={pillItems}
                    onToggle={(key) => {
                      const item = displayItems.find((i) => i.id === key)
                      if (item) toggleItem(item)
                    }}
                  />
                </div>
              ) : (
                <FilterCheckboxList
                  items={itemsBySearch.map((item) => ({
                    key: item.id,
                    label: item.description,
                    count: 'count' in item ? item.count : undefined,
                    checked: selectedItems.some(
                      (selectedItem) => selectedItem.id === item.id
                    ),
                  }))}
                  searchValue={searchValue}
                  searchPlaceholder={searchInputPlaceholder}
                  onSearchChange={handleSearchItems}
                  onToggle={(key) => {
                    const item = displayItems.find((i) => i.id === key)
                    if (item) toggleItem(item)
                  }}
                  highlightQuery={searchQuery}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default FilterWithList
